import * as ConfiguracaoInscricaoCadastralService from '../service/ConfiguracaoInscricaoCadastralService';
import {
  CampoInscricaoCadastral,
  ConfiguracaoInscricaoCadastral
} from '../type';

export type CampoLocalizacao = {
  /** Chave do campo na configuração de Inscrição Cadastral do Tributos. */
  campo: CampoInscricaoCadastral;
  /** Rótulo a exibir — vem da configuração do município, com fallback para o padrão. */
  label: string;
  /** Nome do atributo no cadastro (imobiliário ou rural) replicado no ITBI. */
  campoCadastro: string;
  /** Descrição cadastrada pelo município; vazia quando o campo usa o nome padrão. */
  descricao?: string;
};

/**
 * Ordem em que os campos compõem a inscrição cadastral. É fixa no Tributos
 * (ConfiguracaoInscricaoCadastral.formatarInscricaoCadastral), não é parametrizável.
 */
const CAMPOS: CampoLocalizacao[] = [
  { campo: 'quadricula', label: 'Quadrícula', campoCadastro: 'quadricula' },
  { campo: 'distrito', label: 'Distrito', campoCadastro: 'distrito' },
  // O setor da inscrição cadastral é replicado no ITBI sob o nome "zona".
  { campo: 'setor', label: 'Setor/Zona', campoCadastro: 'zona' },
  { campo: 'quadra', label: 'Quadra', campoCadastro: 'quadra' },
  { campo: 'lote', label: 'Lote', campoCadastro: 'lote' },
  { campo: 'unidade', label: 'Unidade', campoCadastro: 'unidade' }
];

/**
 * Campos de localização do cadastro rural, na ordem do cadastro rural do Tributos. O rural
 * não tem inscrição cadastral própria, mas o Tributos aplica a ele a mesma configuração.
 */
const CAMPOS_RURAIS: CampoLocalizacao[] = [
  { campo: 'distrito', label: 'Distrito', campoCadastro: 'distrito' },
  { campo: 'setor', label: 'Setor', campoCadastro: 'setor' },
  { campo: 'lote', label: 'Lote', campoCadastro: 'lote' }
];

/** Descrição de cada campo ativo no município, indexada pela chave da configuração. */
type DescricoesAtivas = Partial<Record<CampoInscricaoCadastral, string>>;

/**
 * Regra única do urbano e do rural: da lista base, ficam só os campos ativos, com a
 * descrição do município como rótulo e o nome da base quando não há descrição.
 */
const aplicarConfiguracao = (
  base: CampoLocalizacao[],
  ativos: DescricoesAtivas
): CampoLocalizacao[] =>
  base
    .filter(({ campo }) => campo in ativos)
    .map(campoBase => ({
      ...campoBase,
      descricao: ativos[campoBase.campo],
      label: ativos[campoBase.campo] || campoBase.label
    }));

const getDescricoesAtivas = (
  camposLocalizacao: CampoLocalizacao[]
): DescricoesAtivas =>
  camposLocalizacao.reduce(
    (ativos, { campo, descricao }) => ({ ...ativos, [campo]: descricao ?? '' }),
    {} as DescricoesAtivas
  );

/**
 * Campos de localização do imóvel urbano que devem ser exibidos, na ordem da inscrição.
 *
 * Sem configuração carregada (ex.: Tributos indisponível), devolve todos os campos —
 * mantém o comportamento anterior à parametrização em vez de esconder informação.
 */
export const getCamposLocalizacaoAtivos = (
  configuracao?: ConfiguracaoInscricaoCadastral
): CampoLocalizacao[] => {
  if (!configuracao) {
    return CAMPOS;
  }

  const ativos = CAMPOS.filter(
    ({ campo }) => configuracao[campo]?.ativo === true
  ).reduce(
    (descricoes, { campo }) => ({
      ...descricoes,
      [campo]: configuracao[campo]?.descricao ?? ''
    }),
    {} as DescricoesAtivas
  );

  return aplicarConfiguracao(CAMPOS, ativos);
};

/** Campos de localização do cadastro rural a exibir, a partir dos campos ativos carregados. */
export const getCamposLocalizacaoRural = (
  camposLocalizacao: CampoLocalizacao[] = []
): CampoLocalizacao[] =>
  aplicarConfiguracao(CAMPOS_RURAIS, getDescricoesAtivas(camposLocalizacao));

/**
 * Rótulo de cada campo da base, indexado pelo nome do atributo no cadastro: a descrição do
 * município para os campos ativos e o nome da base para os demais (ex.: alterações antigas
 * de um campo hoje inativo, no histórico).
 */
const getRotulos = (
  base: CampoLocalizacao[],
  camposLocalizacao: CampoLocalizacao[] = []
): Record<string, string> =>
  [
    ...base,
    ...aplicarConfiguracao(base, getDescricoesAtivas(camposLocalizacao))
  ].reduce(
    (rotulos, { campoCadastro, label }) => ({
      ...rotulos,
      [campoCadastro]: label
    }),
    {} as Record<string, string>
  );

const prefixar = (rotulos: Record<string, string>, caminhos: string[]) =>
  caminhos.reduce(
    (nomes, caminho) =>
      Object.keys(rotulos).reduce(
        (nomesCaminho, campoCadastro) => ({
          ...nomesCaminho,
          [`${caminho}_${campoCadastro}`]: rotulos[campoCadastro]
        }),
        nomes
      ),
    {} as Record<string, string>
  );

/**
 * Nomes dos campos de localização para o histórico de alterações, indexados pelo caminho
 * do atributo (ex.: "cadastroImobiliario_lote"), para o cadastro urbano e o rural.
 */
export const getNomesCamposLocalizacao = (
  camposLocalizacao: CampoLocalizacao[] | undefined,
  caminhos: { urbano?: string[]; rural?: string[] }
): Record<string, string> => ({
  ...prefixar(getRotulos(CAMPOS, camposLocalizacao), caminhos.urbano ?? []),
  ...prefixar(
    getRotulos(CAMPOS_RURAIS, camposLocalizacao),
    caminhos.rural ?? []
  )
});

/** Campo da configuração que cada filtro de pesquisa representa, com o sufixo do rótulo. */
export type CamposDosFiltros = Record<
  string,
  { campo: CampoInscricaoCadastral; sufixo?: string }
>;

/**
 * Filtros de pesquisa com a mesma regra das telas: os de localização usam a descrição do
 * município (mantendo o sufixo, ex.: " (Código)") e somem quando o campo está inativo; os
 * demais filtros ficam como estão.
 */
export const aplicarConfiguracaoFiltros = <
  T extends { name: string; label: string }
>(
  filtros: T[],
  camposDosFiltros: CamposDosFiltros,
  camposLocalizacao: CampoLocalizacao[]
): T[] =>
  filtros.reduce((resultado, filtro) => {
    const campoDoFiltro = camposDosFiltros[filtro.name];

    if (!campoDoFiltro) {
      return [...resultado, filtro];
    }

    const ativo = camposLocalizacao.find(
      ({ campo }) => campo === campoDoFiltro.campo
    );

    return ativo
      ? [
          ...resultado,
          { ...filtro, label: `${ativo.label}${campoDoFiltro.sufixo ?? ''}` }
        ]
      : resultado;
  }, [] as T[]);

/**
 * Carrega a configuração do município e devolve os campos de localização ativos.
 * Sem a configuração (ex.: Tributos indisponível), devolve todos os campos com o nome padrão.
 */
export const carregarCamposLocalizacao = (): Promise<CampoLocalizacao[]> =>
  ConfiguracaoInscricaoCadastralService.loadConfiguracao()
    .then(response => response.data)
    .catch(() => undefined)
    .then(getCamposLocalizacaoAtivos);
